import { useMemo, useState } from 'react';
import { AlertTriangle, Bell, ChevronDown, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import type { DashboardNotificationItem, DashboardNotificationSeverity, DashboardNotificationSource } from '../types';
import { cn } from '../utils/cn';
import { NotificationDetailModal } from './notifications/NotificationDetailModal';
import { NotificationRow } from './notifications/NotificationRow';
import { SeverityTabs, defaultSeverity } from './notifications/SeverityTabs';
import {
    NOTIFICATION_SEVERITIES,
    NOTIFICATION_SOURCES,
    bySource,
    countBySeverity,
    countKeys,
    severityDots,
    severityIconClasses,
    severityLabels,
    sourceIcons,
    sourceLabels,
} from './notifications/notificationMeta';
import { useNotifications } from './notifications/useNotifications';
import { Button } from './ui';

export { NOTIFICATION_REFRESH_MS } from './notifications/useNotifications';

export default function NotificationBell() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState<DashboardNotificationSource | null>(null);
    const [severityTab, setSeverityTab] = useState<DashboardNotificationSeverity>('high');
    const [detail, setDetail] = useState<DashboardNotificationItem | null>(null);
    const { data, loading, refreshIfStale, markRead, toggleStar, setPriority } = useNotifications({ poll: true });

    const grouped = useMemo(() => bySource(data.items), [data.items]);
    const sourceCount = (source: DashboardNotificationSource) =>
        Math.max(data.counts[countKeys[source]] ?? 0, grouped[source]?.length ?? 0);
    const unreadCount = (source: DashboardNotificationSource) =>
        data.unreadCounts?.[countKeys[source]] ?? (grouped[source] ?? []).filter(item => !item.read).length;
    const activeSources = NOTIFICATION_SOURCES.filter(source => sourceCount(source) > 0);
    const unread = data.unread ?? data.total;
    const hasNotifications = data.total > 0;
    const ariaLabel = unread > 0
        ? `Thông báo: ${unread} chưa xem`
        : hasNotifications ? `Thông báo: ${data.total} cảnh báo, đã xem hết` : 'Thông báo: không có cảnh báo';
    // Detail is read from the live list so star and priority changes show at once.
    const detailItem = detail
        ? [...data.items, ...(data.starred ?? [])].find(item => item.id === detail.id) ?? detail
        : null;

    const goTo = (href: string) => {
        setOpen(false);
        setDetail(null);
        navigate(href);
    };

    // Opening a notification counts as seeing it, like opening an email.
    const openDetail = (item: DashboardNotificationItem) => {
        setOpen(false);
        setDetail(item);
        markRead([item]);
    };

    return (
        <div className="relative">
            <Button
                type="button"
                variant="secondary"
                size="icon"
                className="relative bg-white"
                icon={loading ? <Loader2 className="h-4 w-4 animate-spin text-ink-500" /> : <Bell className="h-4 w-4 text-ink-600" />}
                aria-label={ariaLabel}
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => {
                    if (!open) {
                        refreshIfStale();
                        // With a single group there's nothing to choose: open it right away.
                        const only = activeSources.length === 1 ? activeSources[0] : null;
                        setExpanded(only);
                        if (only) setSeverityTab(defaultSeverity(grouped[only] ?? []));
                    }
                    setOpen(current => !current);
                }}
            />
            {unread > 0 ? (
                <span className="pointer-events-none absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-danger-600 px-1.5 text-[11px] font-semibold leading-none text-white ring-2 ring-white">
                    {unread > 99 ? '99+' : unread}
                </span>
            ) : null}

            {open ? (
                <>
                    <button type="button" className="fixed inset-0 z-40 cursor-default" aria-label="Đóng thông báo" onClick={() => setOpen(false)} />
                    <div
                        role="menu"
                        aria-label="Danh sách thông báo"
                        className="fixed left-3 right-3 top-16 z-50 max-h-[calc(100vh-5rem)] overflow-hidden rounded-xl border border-ink-200 bg-white shadow-raised sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[min(26rem,calc(100vw-1.5rem))]"
                    >
                        <div className="flex items-center justify-between gap-3 border-b border-ink-200 px-4 py-3">
                            <div>
                                <p className="text-sm font-semibold text-ink-900">Thông báo</p>
                                <p className="text-xs text-ink-500">
                                    {hasNotifications ? `${unread} chưa xem · ${data.total} tất cả` : 'Không có gì cần xử lý'}
                                </p>
                            </div>
                            <Button variant="secondary" size="sm" className="min-h-8" onClick={() => goTo('/notifications')}>
                                Xem tất cả
                            </Button>
                        </div>

                        {loading ? (
                            <div className="flex min-h-32 items-center justify-center gap-2 px-4 py-6 text-sm text-ink-500">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Đang tải thông báo
                            </div>
                        ) : hasNotifications ? (
                            <div className="max-h-[min(32rem,calc(100vh-8rem))] divide-y divide-ink-100 overflow-y-auto">
                                {activeSources.map(source => {
                                    const items = grouped[source] ?? [];
                                    const total = sourceCount(source);
                                    const sourceUnread = unreadCount(source);
                                    const severityCounts = countBySeverity(items);
                                    const isExpanded = expanded === source;
                                    const topSeverity = NOTIFICATION_SEVERITIES.find(severity => severityCounts[severity] > 0) ?? 'low';
                                    const Icon = sourceIcons[source];
                                    const tabItems = items.filter(item => item.severity === severityTab);
                                    const unreadItems = tabItems.filter(item => !item.read);

                                    return (
                                        <section key={source}>
                                            <button
                                                type="button"
                                                aria-expanded={isExpanded}
                                                onClick={() => {
                                                    setExpanded(isExpanded ? null : source);
                                                    if (!isExpanded) setSeverityTab(defaultSeverity(items));
                                                }}
                                                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-ink-50 focus:outline-none focus-visible:bg-academy-50"
                                            >
                                                <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1', severityIconClasses[topSeverity])}>
                                                    <Icon className="h-4 w-4" />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm font-semibold text-ink-900">{sourceLabels[source]}</span>
                                                    <span className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-ink-500">
                                                        {NOTIFICATION_SEVERITIES.filter(severity => severityCounts[severity] > 0).map(severity => (
                                                            <span key={severity} className="inline-flex items-center gap-1">
                                                                <span className={cn('h-1.5 w-1.5 rounded-full', severityDots[severity])} />
                                                                {severityCounts[severity]} {severityLabels[severity].toLowerCase()}
                                                            </span>
                                                        ))}
                                                    </span>
                                                </span>
                                                {sourceUnread > 0 ? (
                                                    <span className="rounded-full bg-danger-600 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white" aria-label={`${sourceUnread} chưa xem`}>
                                                        {sourceUnread}
                                                    </span>
                                                ) : null}
                                                <span className="rounded bg-ink-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-ink-700">{total}</span>
                                                <ChevronDown className={cn('h-4 w-4 shrink-0 text-ink-400 transition-transform', isExpanded && 'rotate-180')} />
                                            </button>

                                            {isExpanded ? (
                                                <div className="pb-2">
                                                    <SeverityTabs items={items} active={severityTab} onChange={setSeverityTab} className="px-2" />
                                                    {unreadItems.length ? (
                                                        <div className="flex justify-end px-4 pt-2 text-xs">
                                                            <button type="button" onClick={() => markRead(unreadItems)} className="font-medium text-academy-700 hover:underline">
                                                                Đánh dấu đã xem tất cả ({unreadItems.length})
                                                            </button>
                                                        </div>
                                                    ) : null}
                                                    <div className="space-y-0.5 px-2 pt-1">
                                                        {tabItems.map(item => (
                                                            <NotificationRow
                                                                key={item.id}
                                                                item={item}
                                                                onOpen={openDetail}
                                                                onMarkRead={entry => markRead([entry])}
                                                                onToggleStar={toggleStar}
                                                            />
                                                        ))}
                                                    </div>
                                                    {total > items.length ? (
                                                        <div className="flex justify-end px-4 pt-1 text-xs">
                                                            <button type="button" onClick={() => goTo(`/notifications?group=${source}`)} className="font-medium text-academy-700 hover:underline">
                                                                Đang hiện {items.length}/{total} · Xem tất cả
                                                            </button>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </section>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex min-h-32 flex-col items-center justify-center gap-2 px-4 py-6 text-center">
                                <AlertTriangle className="h-5 w-5 text-ink-400" />
                                <p className="text-sm font-medium text-ink-700">Không có thông báo cần xử lý.</p>
                            </div>
                        )}
                    </div>
                </>
            ) : null}

            <NotificationDetailModal
                item={detailItem}
                canChangePriority={user?.role === 'ADMIN' || user?.role === 'MANAGER'}
                onClose={() => setDetail(null)}
                onGoTo={item => goTo(item.href)}
                onSetPriority={setPriority}
                onToggleStar={toggleStar}
                onMarkUnread={item => markRead([item], false)}
            />
        </div>
    );
}
